import store from '@/store';
import { t } from '@shared/helpers/translation';
import { AnyProduct } from '@shared/interfaces/product';
import {
    Cart,
    Customer,
    FerryLeg,
    FerryProduct,
    FlightLeg,
    FlightPax,
    FlightProduct,
    HotelProduct,
    PaxDescription,
    SerializationType,
    ProductType,
    TransferLocation,
    TransferMainPassenger,
    TransferProduct,
    ActivityProduct,
    GeoLocation,
} from '@/api/Cart/cart.interfaces';
import {
    SearchLocationCoordinates,
    SearchLocationType,
    SearchPassengers,
    SearchTransferRequest,
    TransferOption,
} from '@/api/Transfer/transfer.interfaces';
import { Money } from '@libs/money/money.interfaces';
import { generateUUID } from '@shared/utils/uuid';
import { convertToDate, getShortDate } from '@shared/helpers/datetime/datetime';
import { isNotNil } from '@shared/utils/value';
import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
import { parseEnumValue } from '@shared/utils/enum';
import { isEmpty, isNil, isEqual, orderBy } from 'lodash';
import { getErrorMessage, handleCustomError } from '@/config/errorHandling';
import {
    AnyTransferProduct,
    JourneyItem,
    TransferProductType,
    TransferConnection,
    TransferImportantMessage,
    TransferJourneyType,
    TransferSearchFlow,
    TransferTrip,
    TransferTripStatus,
    JourneyPoint,
} from './transfer.interfaces';
import { SUPPORTED_TRANSFER_TYPES } from './transfer.consts';
import { getTransferTimes } from '@/helpers/transfer/transfer.times';
import { endpoints } from '@/api/endpoints';
import { Airport } from '@/api/Content/content.interfaces';
import { SelectItem } from '@ui/components/FormV2/Shared/interfaces/Select.interfaces';
import {
    getSupportedDynamicConnectionProducts,
    getSupportedTransferTypes,
} from '@/helpers/transfer/transfer.config';

export async function getTransferTrips(cart: Cart): Promise<TransferTrip[]> {
    const market = store.state.general.market.locale;

    const transferProducts = (
        cart.Products.filter(isTransferProduct) as AnyTransferProduct[]
    ).filter(validateProductLocation);

    const passengers = getPassengers(transferProducts);
    const journeyItems = await getJourneyItems(transferProducts);

    const transferConnections = createTransferConnections(journeyItems)
        .filter(hasDistinctLocations)
        .filter(isSupportedTransferJourneyType);

    const connections = addDynamicTransferConnections(
        transferConnections,
        journeyItems
    ).filter(isSupportedTransferJourneyType);

    return connections.map((c) => generateTrip(c, passengers, market));
}

function hasDistinctLocations(connection: TransferConnection) {
    return !isEqual(connection.from.location, connection.to.location);
}

function validateProductLocation(product: AnyTransferProduct) {
    switch (product.Type) {
        case ProductType.Hotel: {
            const hotel = product as HotelProduct;
            return hotel.Address && hotel?.Position?.Latitude && hotel?.Position?.Longitude;
        }
        default:
            return true;
    }
}

function addDynamicTransferConnections(
    connections: TransferConnection[],
    journeys: JourneyItem[]
): TransferConnection[] {
    let newConnections = [...connections];
    const supportedProducts = getSupportedDynamicConnectionProducts();

    const isSupported = (journey?: JourneyItem) =>
        !!journey && supportedProducts.includes(journey.type);

    const first = journeys[0];
    if (isSupported(first)) {
        const fromConnection = addIataConnection(first, 'from');

        if (fromConnection) {
            newConnections = [fromConnection, ...newConnections];
        }
    }

    const last = journeys[journeys.length - 1];
    if (isSupported(last)) {
        const toConnection = addIataConnection(last, 'to');

        if (toConnection) {
            newConnections = [...newConnections, toConnection];
        }
    }

    return newConnections;
}

function addIataConnection(
    journey: JourneyItem,
    direction: 'from' | 'to'
): TransferConnection | undefined {
    const hasAirports = (journey.closestAirports?.length ?? 0) > 0;

    if (hasAirports) {
        const baseIataJourney = {
            id: generateUUID(),
        };

        if (direction === 'from') {
            return {
                from: {
                    ...baseIataJourney,
                    productId: `airport.${journey.productId}`,
                    date: journey.start.date,
                    description: null,
                    productType: TransferProductType.Airport,
                    location: { type: SearchLocationType.Iata, iata: undefined },
                    closestAirports: journey.closestAirports,
                },
                to: journey.start,
            };
        }

        return {
            from: journey.end,
            to: {
                ...baseIataJourney,
                productId: `${journey.productId}.airport`,
                date: journey.end.date,
                description: null,
                productType: TransferProductType.Airport,
                location: { type: SearchLocationType.Iata, iata: undefined },
                closestAirports: journey.closestAirports,
            },
        };
    }

    return undefined;
}

function createTransferConnections(journeys: JourneyItem[]): TransferConnection[] {
    const transferConnections = [];
    for (let i = 0; i < journeys.length - 1; i++) {
        const from = journeys[i];
        const to = journeys[i + 1];

        const item: TransferConnection = {
            from: from.end,
            to: to.start,
        };
        transferConnections.push(item);
    }

    return transferConnections;
}

async function getJourneyItems(products: AnyTransferProduct[]): Promise<JourneyItem[]> {
    const itemsArrays = await Promise.all(products.map(createJourneyItem));
    const items = itemsArrays.flat();
    return sortJourneyItems(items);
}

async function createJourneyItem(product: AnyTransferProduct): Promise<JourneyItem[]> {
    const productType = product.Type as unknown as TransferProductType;

    const baseItem = {
        id: generateUUID(),
        productId: product.Id,
        type: productType,
    };

    const mapFlight = ({ Segments: segments }: FlightLeg): JourneyItem => {
        const [from, to] = [segments[0], segments[segments.length - 1]];

        return {
            ...baseItem,
            start: {
                productId: product.Id,
                productType: baseItem.type,
                flightNumber: from.FlightNumber,
                description: from.DepartureAirportName,
                date: convertToDate(from.DepartureDate),
                location: {
                    type: SearchLocationType.Iata,
                    iata: from.DepartureAirportCode,
                },
            },
            end: {
                productId: product.Id,
                productType: baseItem.type,
                flightNumber: to.FlightNumber,
                description: to.ArrivalAirportName,
                date: convertToDate(to.ArrivalDate),
                location: {
                    type: SearchLocationType.Iata,
                    iata: to.ArrivalAirportCode,
                },
            },
        };
    };

    // TODO: needs to update locations type when we support ferries
    const mapFerry = ({ Segments: segments }: FerryLeg): JourneyItem => {
        const [from, to] = [segments[0], segments[segments.length - 1]];
        return {
            ...baseItem,
            start: {
                productId: product.Id,
                productType: baseItem.type,
                description: from.DeparturePortName,
                date: convertToDate(from.DepartureDate),
                location: {
                    type: SearchLocationType.Iata,
                    iata: from.DeparturePortCode,
                },
            },
            end: {
                productId: product.Id,
                productType: baseItem.type,
                description: to.ArrivalPortName,
                location: {
                    type: SearchLocationType.Iata,
                    iata: to.ArrivalPortCode,
                },
                date: convertToDate(to.ArrivalDate),
            },
        };
    };

    const mapHotelAddress = (p: HotelProduct): string | undefined => {
        if (p.Address) {
            const { StreetAddress, City, Country } = p.Address;

            if (StreetAddress && City && Country) {
                return `${StreetAddress}, ${City}, ${Country}`;
            }
        }
        handleCustomError(`Address undefined, checkout transfer, Hotel code: ${p.HotelCode}`);

        return undefined;
    };

    const mapCoordinates = (
        location: GeoLocation | null
    ): SearchLocationCoordinates | undefined => {
        if (location) {
            const { Longitude, Latitude } = location;

            if (Longitude && Latitude) {
                return {
                    longitude: Longitude,
                    latitude: Latitude,
                };
            }
        }

        return undefined;
    };

    const getClosestAirports = async (polygonId: number | null): Promise<Airport[]> => {
        let closestAirports: Airport[] = [];

        if (!polygonId) return [];

        try {
            const response = polygonId
                ? await endpoints.content.GetAirportsByPolygonId(polygonId, 5, true)
                : null;
            if (response) {
                closestAirports = response.map((x) => x.airport);
            }
        } catch (e) {
            handleCustomError(`Transfer getClosestAirports: ${getErrorMessage(e)}`);
        }
        return closestAirports;
    };

    const mapHotel = async (p: HotelProduct): Promise<JourneyItem> => {
        const closestAirports = await getClosestAirports(p.PolygonId);
        const journeyPoint: JourneyPoint = {
            productId: product.Id,
            productType: baseItem.type,
            description: p.Description,
            date: convertToDate(p.FromDate),
            location: {
                type: SearchLocationType.Coordinates,
                address: mapHotelAddress(p),
                coordinates: mapCoordinates(p.Position),
                hotelName: p.Description,
            },
        };

        return {
            ...baseItem,
            closestAirports,
            start: journeyPoint,
            end: {
                ...journeyPoint,
                date: convertToDate(p.ToDate),
            },
        };
    };

    const mapActivity = async (p: ActivityProduct): Promise<JourneyItem> => {
        const closestAirports = await getClosestAirports(p.PolygonId);
        const journeyPoint: JourneyPoint = {
            productId: product.Id,
            productType: baseItem.type,
            description: p.Description,
            date: convertToDate(p.Date),
            location: {
                ...(p.Address && { address: p.Address }),
                type: SearchLocationType.Coordinates,
                coordinates: mapCoordinates(p.Location),
            },
        };

        return {
            ...baseItem,
            closestAirports,
            start: journeyPoint,
            end: journeyPoint,
        };
    };

    switch (product.Type) {
        case ProductType.Hotel:
            return [await mapHotel(product as HotelProduct)];
        case ProductType.Ferry:
            return (product as FerryProduct).Legs.map(mapFerry);
        case ProductType.Flight:
            return (product as FlightProduct).Legs.map(mapFlight);
        case ProductType.Activity:
            return [await mapActivity(product as ActivityProduct)];
        default:
            return [];
    }
}

function sortJourneyItems(items: JourneyItem[]): JourneyItem[] {
    const priorityOrder: TransferProductType[] = [
        TransferProductType.Flight,
        TransferProductType.Ferry,
        TransferProductType.Hotel,
        TransferProductType.Activity,
    ];

    const hasExplicitTime = (date: Date): boolean => {
        return !!(
            date.getHours() ||
            date.getMinutes() ||
            date.getSeconds() ||
            date.getMilliseconds()
        );
    };

    return [...items].sort((a, b) => {
        const dateA = a.start.date;
        const dateB = b.start.date;
        const priorityDiff =
            a.type && b.type
                ? priorityOrder.indexOf(a.type) - priorityOrder.indexOf(b.type)
                : -1;

        // If both items lack a date, use priority only
        if (!dateA && !dateB) return priorityDiff;

        // Items without a date go last
        if (!dateA) return 1;
        if (!dateB) return -1;

        const timeA = dateA.getTime();
        const timeB = dateB.getTime();

        // Same timestamp → sort by priority
        if (timeA === timeB) return priorityDiff;

        const isSameDay = getShortDate(dateA) === getShortDate(dateB);

        if (isSameDay) {
            const aHasTime = hasExplicitTime(dateA);
            const bHasTime = hasExplicitTime(dateB);

            // If only one has a time, prioritize it accordingly
            if (!aHasTime && bHasTime) return priorityDiff;
            if (aHasTime && !bHasTime) return -priorityDiff;
        }

        // Default to chronological sort
        return timeA - timeB;
    });
}

function getTransferSearchFlow(connection: TransferConnection): TransferSearchFlow {
    const { from, to } = connection;

    if (
        to.productType === TransferProductType.Airport ||
        from.productType === TransferProductType.Airport
    ) {
        return TransferSearchFlow.Airport;
    }

    if (
        to.productType === TransferProductType.Flight ||
        from.productType === TransferProductType.Flight
    ) {
        return TransferSearchFlow.Flight;
    }

    return TransferSearchFlow.Custom;
}

function generateTrip(
    connection: TransferConnection,
    passengers: SearchPassengers,
    market: string
): TransferTrip {
    const { from, to } = connection;
    const description: string = t({
        key: 'shared.from_a_to_b',
        values: [from.description, to.description],
    });

    const type = getTransferJourneyType(from.productType, to.productType);
    const searchFlow = getTransferSearchFlow(connection);
    const transferTimes = getTransferTimes(connection, searchFlow);

    const request: SearchTransferRequest = {
        passengers: passengers,
        market: market,
        from: from.location,
        to: to.location,
        ...transferTimes,
    };

    const icons = getTransferJourneyIcons(from.productType, to.productType);

    const tripId = `${from.productId}.${to.productId}`;

    return {
        id: tripId,
        status: TransferTripStatus.INITIAL,
        active: false,
        visible: false,
        type,
        icons,
        request,
        description,
        from,
        to,
        searchFlow,
    };
}

export function getJourneyDescription(journey: JourneyPoint, showType?: boolean) {
    if (showType && journey.productType === TransferProductType.Airport) {
        return t({ key: 'shared.airport' });
    }

    return journey.description;
}

export function getTripLocationDescription(trip: TransferTrip, showType?: boolean) {
    return `${getJourneyDescription(trip.from, showType)} - ${getJourneyDescription(trip.to, showType)}`;
}

function getTransferJourneyType(
    from: TransferProductType,
    to: TransferProductType
): TransferJourneyType {
    return parseEnumValue(`${from}${to}`, TransferJourneyType) ?? TransferJourneyType.Unknown;
}

function getTransferJourneyIcons(
    from: TransferProductType,
    to: TransferProductType
): IconType[] {
    return [getTransferProductIcon(from), getTransferProductIcon(to)].filter(isNotNil);
}

function getTransferProductIcon(type: TransferProductType): IconType | undefined {
    switch (type) {
        case TransferProductType.Hotel:
            return 'Bed';
        case TransferProductType.Flight:
        case TransferProductType.Airport:
            return 'Plane';
        case TransferProductType.Ferry:
            return 'Ferry';
        case TransferProductType.Activity:
            return 'Ticket';
        default:
            return undefined;
    }
}
export function mapTransferPrice(option: TransferOption): Money {
    return {
        Amount: option.Price.Amount,
        Currency: option.Price.Currency,
    };
}

export function getAirportsOptions(airports: Airport[]): SelectItem[] {
    return (airports || [])
        .map((x) => {
            if (x && x.code && x.labelShort) {
                return {
                    label: x.labelShort,
                    value: x.code,
                };
            }
        })
        .filter(isNotNil);
}

export function getValidTransferProductId(trip: TransferTrip): string | undefined {
    const excluded = [TransferProductType.Airport];
    const products = [trip.from, trip.to];

    const product = products.find((p) => p?.productId && !excluded.includes(p.productType));

    return product?.productId;
}

export function getMainPassenger(
    product: AnyTransferProduct | undefined,
    customer: Customer
): TransferMainPassenger | null {
    if (!product) return null;

    let passenger: TransferMainPassenger = {
        FirstName: '',
        LastName: '',
        Phone: customer?.PhoneNumber ?? '',
        Email: customer?.Email ?? '',
    };

    switch (product.Type) {
        case ProductType.Hotel: {
            const hotel = product as HotelProduct;
            const room = hotel.Rooms[0];
            if (room) {
                passenger = {
                    ...passenger,
                    FirstName: room.Pax.FirstName ?? '',
                    LastName: room.Pax.LastName ?? '',
                };
            }
            break;
        }
        case ProductType.Flight: {
            const flight = product as FlightProduct;
            const pax = flight.Pax.find((x: FlightPax) => x.PaxDescription?.IsAdult);
            if (pax) {
                passenger = {
                    ...passenger,
                    FirstName: pax.FirstName ?? '',
                    LastName: pax.LastName ?? '',
                };
            }
            break;
        }
        case ProductType.Activity: {
            const activity = product as ActivityProduct;
            const pax = activity.Tickets.flatMap((t) => t.Pax).find((p) => p.FirstName);
            if (pax) {
                passenger = {
                    ...passenger,
                    FirstName: pax.FirstName ?? '',
                    LastName: pax.LastName ?? '',
                };
            }
            break;
        }
    }

    if (Object.values(passenger).some((value) => isNil(value) || isEmpty(value))) {
        return null;
    }

    return passenger;
}

export function getShieldText(passenger: TransferMainPassenger | null): string | null {
    if (passenger && passenger.FirstName && passenger.LastName) {
        return `${passenger.FirstName} ${passenger.LastName}`;
    }

    return null;
}

export function mapToTransferProduct(
    option: TransferOption,
    trip: TransferTrip
): TransferProduct {
    const validProductId = getValidTransferProductId(trip);
    const product = store.getters['cart/getProduct'](validProductId);
    const customer = store.getters['cart/getCustomer'];

    setStoredTransferTrip(trip);

    // Add getMainPassengerProduct
    const mainPassenger: TransferMainPassenger | null = getMainPassenger(product, customer);

    const startPoint: TransferLocation = {
        Iata: trip.from.location.iata,
        Address: trip.from.location.address,
        Type: trip.from.location.type,
        Coordinates: {
            Latitude: trip.from.location.coordinates?.latitude,
            Longitude: trip.from.location.coordinates?.longitude,
        },
        HotelName: trip.from.location.hotelName,
    };

    const endPoint: TransferLocation = {
        Iata: trip.to.location.iata,
        Address: trip.to.location.address,
        Type: trip.to.location.type,
        Coordinates: {
            Latitude: trip.to.location.coordinates?.latitude,
            Longitude: trip.to.location.coordinates?.longitude,
        },
        HotelName: trip.to.location.hotelName,
    };

    return {
        $type: SerializationType.TransferProduct,
        Id: generateUUID(),
        TripId: trip.id,
        Type: ProductType.Transfer,
        Price: mapTransferPrice(option),
        FlightNumber: (trip.from.flightNumber || trip.to.flightNumber) ?? null,
        AppliedDiscount: null,
        Tags: [],
        Description: trip.description,
        OfferId: option.Id,
        WheelChairsPlaces: 0,
        AnimalsPlaces: 0,
        Passengers: {
            Adults: trip.request.passengers.adults,
            Children: trip.request.passengers.children,
            Infants: trip.request.passengers.infants,
        },
        MainPassenger: mainPassenger,
        Comment: null,
        PickUpTime: trip.request.startTime,
        ShieldText: getShieldText(mainPassenger),
        Extras: null,
        From: startPoint,
        To: endPoint,
        Luggage: option.MaxLargeLuggage ?? 0,
        Distance: option.Distance ?? 0,
        Duration: option.Duration ?? 0,
        TollsIncluded: option.TollsIncluded,
        GratuityIncluded: option.GratuityIncluded,
        DriverWaitingTime: option.DriverWaitingTime ?? 0,
        FreeCancelUntil: option.FreeCancelUntil,
        Vehicle: {
            Image: option.TransferVehicle.Image,
            Model: option.TransferVehicle.Model,
            TransferCategory: option.TransferVehicle.CategoryType,
            ServiceType: option.TransferVehicle.ServiceType,
        },
        Partner: {
            Image: option.TransferPartner.Logo,
            Name: option.TransferPartner.Name,
        },
        CancellationPolicyText: option.CancellationPolicyText,
        TransferType: option.TransferType,
        JourneyType: trip.type,
        DisclaimerTitle: option.DisclaimerTitle,
        DisclaimerDescription: option.DisclaimerDescription,
        SupplierPrice: option?.SupplierPrice,
        IsSharedTransfer: option.IsSharedTransfer,
    };
}

export function cleanTransferImage(src: string) {
    if (src.includes('@')) {
        return src.split('@')[0];
    }

    return src;
}

function getPaxDescriptions(product?: AnyTransferProduct): PaxDescription[] {
    switch (product?.Type) {
        case ProductType.Hotel:
            return (product as HotelProduct).Rooms.flatMap((r) => r.PaxDescription);
        case ProductType.Ferry:
        case ProductType.Flight: {
            const p = product as FerryProduct | FlightProduct;
            const paxDescriptions = p.Pax.map((pax) => pax.PaxDescription).filter(isNotNil);

            const isValid = paxDescriptions.length === p.Pax.length;
            return isValid ? paxDescriptions : [];
        }
        default:
            return [];
    }
}

function getPassengers(products: AnyTransferProduct[]): SearchPassengers {
    const ageConfig = {
        child: { min: 2, max: 12 },
        infant: { min: 0, max: 1 },
    };

    const productTypes = [ProductType.Flight, ProductType.Ferry, ProductType.Hotel];

    const [product] = products
        .filter((p) => productTypes.includes(p.Type))
        .sort((a, b) => productTypes.indexOf(a.Type) - productTypes.indexOf(b.Type));

    if (!product) {
        const [activity] = orderBy(
            products.filter((a) => a.Type === ProductType.Activity),
            ['Tickets.length']
        );

        if (activity) {
            return {
                adults: (activity as ActivityProduct).Tickets.length,
                children: 0,
                infants: 0,
            };
        }
    }

    const paxDescriptions: PaxDescription[] = getPaxDescriptions(product);

    const passengers = paxDescriptions.reduce(
        (counts, pax) => {
            const age = pax.Age ?? (pax.IsAdult ? 99 : 0);
            if (age >= ageConfig.child.min && age <= ageConfig.child.max) {
                counts.children++;
            } else if (age >= ageConfig.infant.min && age <= ageConfig.infant.max) {
                counts.infants++;
            } else {
                counts.adults++;
            }
            return counts;
        },
        { adults: 0, children: 0, infants: 0 }
    );
    return passengers;
}

export function getStaticTransferTerms(): TransferImportantMessage[] {
    return [
        {
            title: t({ key: 'shared.important_information' }),
            description: t({ key: 'shared.transfer_vehicle_disclaimer' }),
        },
    ];
}

function isTransferProduct(product: AnyProduct) {
    const transferTypes = getSupportedTransferTypes();
    return transferTypes.includes(product.Type);
}

function isSupportedTransferJourneyType({ from, to }: TransferConnection): boolean {
    const type = getTransferJourneyType(from.productType, to.productType);
    return type ? SUPPORTED_TRANSFER_TYPES.includes(type) : false;
}

function getStorageTripKey(id: string) {
    return `TransferTrip:${id}`;
}

export function setStoredTransferTrip(trip: TransferTrip) {
    localStorage.setItem(getStorageTripKey(trip.id), JSON.stringify(trip));
}

export function getStoredTransferTrip(tripId: string): TransferTrip | null {
    const raw = localStorage.getItem(getStorageTripKey(tripId));
    if (!raw) return null;

    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}
